import { observer } from 'mobx-react-lite';
import { Button, Tabs, Tab, TextField, IconButton, Checkbox, FormControlLabel } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { CopilotStore } from '../CopilotStore';
import { Alert, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId } from '@material-ui/data-grid';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import StepConfirmation, { ConfirmationDialogProps, defaultConfirmationDialogProps } from '../../controls/StepConfirmation';
import AquantWarningIcon from '../../../assets/aq-warning.svg';
import { 
    ObservationSuggestion, 
    HighlightSuggestion,
    ObservationSuggestionModel,
    HighlightSuggestionModel,
} from '../models';
import TabPanel from '../../common/TabPanel';
import { SelectionItem, SIAutocomplete } from '../../controls/Autocomplete';

type CustomerSuggestionsProps = {
    store: CopilotStore
}

export const CustomerSuggestions = observer(({ store }: CustomerSuggestionsProps) => {

    const {
        selectedCustomer: customer,
        models,
        selectedModel,
        setSelectedModel,
        fetchObservationSuggestions,
        upsertObservationSuggestions,
        fetchHighlightSuggestions,
        updateHighlightSuggestions,
        deleteObservationSuggestions,
        deleteHighlightSuggestions
    } = store

    // Main tab state
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [highlightLoading, setHighlightLoading] = React.useState<boolean>(false);

    // Suggestion state
    const [observationSuggestions, setObservationSuggestions] = useState<ObservationSuggestion[]>([]);
    const [highlightSuggestions, setHighlightSuggestions] = useState<HighlightSuggestion[]>([]);
    const [selectedObservationIds, setSelectedObservationIds] = useState<GridRowId[]>([]);
    const [selectedHighlightIds, setSelectedHighlightIds] = useState<GridRowId[]>([]);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteDialogProps, setDeleteDialogProps] = useState<ConfirmationDialogProps | undefined>(undefined);
    
    const [newObservation, setNewObservation] = useState('');
    const [newObservationSuggestion, setNewObservationSuggestion] = useState('');

    // Save state
    const [saveObservationsLoading, setSaveObservationsLoading] = useState<boolean>(false);
    const [saveHighlightsLoading, setSaveHighlightsLoading] = useState<boolean>(false);
    const [observationEdited, setObservationEdited] = useState<boolean>(false);
    const [highlightEdited, setHighlightEdited] = useState<boolean>(false);

    // Add state for validation error
    const [validationError, setValidationError] = useState<string | null>(null);

    // Add state to track created and updated items
    const [createdObservations, setCreatedObservations] = useState<ObservationSuggestion[]>([]);
    const [updatedObservations, setUpdatedObservations] = useState<ObservationSuggestion[]>([]);
    const [updatedHighlights, setUpdatedHighlights] = useState<HighlightSuggestion[]>([]);

    const [allModelsEnabled, setAllModelsEnabled] = useState(false);
    const [allModels, setAllModels] = useState(false);
    const observationModel: string | null | undefined = allModels ? null : selectedModel?.value;
    const scopeKey = `${customer?.name}|${observationModel}`;
    const scopeKeyRef = useRef(scopeKey);
    scopeKeyRef.current = scopeKey;

    useEffect(() => {
        setAllModels(false);
        setAllModelsEnabled(false);
        if (!customer) return;
        let ignore = false;
        fetchObservationSuggestions(customer.name, null)
            .then(response => {
                if (!ignore) setAllModelsEnabled(response.enabled === true);
            });
        return () => {
            ignore = true;
        };
    }, [customer]);

    // Add this function after the validationError state declaration
    const handleValidationError = (message: string) => {
        setValidationError(message);
        setTimeout(() => setValidationError(null), 3000);
    };

    useEffect(() => {
        setCreatedObservations([]);
        setUpdatedObservations([]);
        setObservationEdited(false);
        if (!customer || observationModel === undefined) {
            setObservationSuggestions([]);
            setLoading(false);
            return;
        }
        let ignore = false;
        setLoading(true);
        fetchObservationSuggestions(customer.name, observationModel)
            .then(response => {
                if (ignore) return;
                setObservationSuggestions(response.data.map((item: ObservationSuggestionModel): ObservationSuggestion => ({
                    ...item,
                    id: item.rowid || String(Math.random())
                })));
            })
            .catch(error => {
                console.error("Error fetching suggestions:", error);
                if (!ignore) setObservationSuggestions([]);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [customer, observationModel]);

    useEffect(() => {
        if (!customer || !selectedModel) {
            setHighlightSuggestions([]);
            setHighlightLoading(false);
            return;
        }
        let ignore = false;
        setHighlightLoading(true);
        fetchHighlightSuggestions(customer.name, selectedModel.value)
            .then(response => {
                if (ignore) return;
                setHighlightSuggestions(response.data.map((item: HighlightSuggestionModel): HighlightSuggestion => ({
                    ...item,
                    id: item.rowid || String(Math.random())
                })));
            })
            .catch(error => {
                console.error("Error fetching suggestions:", error);
                if (!ignore) setHighlightSuggestions([]);
            })
            .finally(() => {
                if (!ignore) setHighlightLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [customer, selectedModel]);

    // Handler for saving observation suggestion edits
    const handleObservationEdit = (id: string, field: string, value: any) => {
        const itemToEdit = observationSuggestions.find(item => item.id === id);
        // Only mark as edited if the value has actually changed
        if (itemToEdit && itemToEdit[field as keyof ObservationSuggestion] !== value) {
            // Update the suggestion
            const updatedItem: ObservationSuggestion = { ...itemToEdit, [field]: value };
            
            setObservationSuggestions(prev => 
                prev.map(item => 
                    item.id === id ? updatedItem : item
                )
            );
            
            // Check if it's a newly created item
            const isNewItem = createdObservations.some((item: ObservationSuggestion) => item.id === id);
            
            if (isNewItem) {
                // Update it in the created items list
                setCreatedObservations(prev =>
                    prev.map((item: ObservationSuggestion) => item.id === id ? updatedItem : item)
                );
            } else {
                // Add or update it in the updated items list
                const alreadyUpdated = updatedObservations.some((item: ObservationSuggestion) => item.id === id);
                if (alreadyUpdated) {
                    setUpdatedObservations(prev =>
                        prev.map((item: ObservationSuggestion) => item.id === id ? updatedItem : item)
                    );
                } else {
                    setUpdatedObservations(prev => [...prev, updatedItem]);
                }
            }
            
            setObservationEdited(true);
        }
    };

    // Handler for saving highlight suggestion edits
    const handleHighlightEdit = (id: string, field: string, value: any) => {
        const itemToEdit = highlightSuggestions.find(item => item.id === id);
        
        // Only mark as edited if the value has actually changed
        if (itemToEdit && itemToEdit[field as keyof HighlightSuggestion] !== value) {
            // Update the suggestion
            const updatedItem: HighlightSuggestion = { ...itemToEdit, [field]: value };
            
            // Update the suggestions list
            setHighlightSuggestions(prev => 
                prev.map(item => 
                    item.id === id ? updatedItem : item
                )
            );
            
            // Remove the old version if it exists and add the new version
            setUpdatedHighlights(prev => {
                const filtered = prev.filter(item => item.id !== id);
                return [...filtered, updatedItem];
            });
            
            setHighlightEdited(true);
        }
    };

    // Update the addObservationSuggestion function
    const addObservationSuggestion = () => {
        if (newObservation && newObservationSuggestion) {
            const newId = String(Math.random());
            const newSuggestion: ObservationSuggestion = {
                observation: newObservation,
                suggestion: newObservationSuggestion,
                id: newId,
                // rowid will be assigned by the backend
            };
            
            setObservationSuggestions([
                ...observationSuggestions,
                newSuggestion
            ]);
            
            // Add to created list
            setCreatedObservations([...createdObservations, newSuggestion]);
            
            setNewObservation('');
            setNewObservationSuggestion('');
            setObservationEdited(true);
        }
    };

    // Update deleteSelectedObservations to use real API
    const deleteSelectedObservations = () => {
        if (observationModel === undefined) return;
        setIsDeleting(true);
        const requestScope = scopeKeyRef.current;
        
        // Get the IDs to delete
        const idsToDelete = selectedObservationIds;
        
        // Filter out any newly created items that haven't been saved yet
        const backendIdsToDelete = observationSuggestions
            .filter(item => idsToDelete.includes(item.id) && item.rowid)
            .map(item => item.rowid as string);
        
        // Make API call if there are items to delete
        if (backendIdsToDelete.length > 0) {
            deleteObservationSuggestions(
                customer!.name, 
                observationModel,
                backendIdsToDelete
            ).then(response => {
                if (requestScope !== scopeKeyRef.current) return;
                if (response.status === 'error') {
                    console.error("Error deleting observations:", response.message);
                    // Show error message
                    handleValidationError(response.message);
                } else {
                    // Only update the state if the server request was successful
                    // Update the state immediately (optimistic update)
                    const remainingItems = observationSuggestions.filter(item => !idsToDelete.includes(item.id));
                    setObservationSuggestions(remainingItems);
                    
                    // Remove deleted items from the created and updated lists
                    setCreatedObservations(prev => prev.filter(item => !idsToDelete.includes(item.id)));
                    setUpdatedObservations(prev => prev.filter(item => !idsToDelete.includes(item.id)));
                    
                    // If everything is deleted, set edited to false
                    if (remainingItems.length === 0 || 
                        (createdObservations.length === 0 && updatedObservations.length === 0)) {
                        setObservationEdited(false);
                    }
                    
                    setSelectedObservationIds([]);
                }
            }).catch(error => {
                console.error("Error in delete API call:", error);
                if (requestScope === scopeKeyRef.current) handleValidationError("Failed to delete observations");
            }).finally(() => {
                setIsDeleting(false);
                setDeleteDialogProps(undefined);
            });
        } else {
            // For items without rowid (newly created), just remove them from the UI
            const remainingItems = observationSuggestions.filter(item => !idsToDelete.includes(item.id));
            setObservationSuggestions(remainingItems);
            
            // Remove deleted items from the created and updated lists
            setCreatedObservations(prev => prev.filter(item => !idsToDelete.includes(item.id)));
            setUpdatedObservations(prev => prev.filter(item => !idsToDelete.includes(item.id)));
            
            // If everything is deleted, set edited to false
            if (remainingItems.length === 0 || 
                (createdObservations.length === 0 && updatedObservations.length === 0)) {
                setObservationEdited(false);
            }
            
            setSelectedObservationIds([]);
            setIsDeleting(false);
            setDeleteDialogProps(undefined);
        }
    };

    // Update deleteSelectedHighlights to use real API
    const deleteSelectedHighlights = () => {
        setIsDeleting(true);
        
        // Get the IDs to delete
        const idsToDelete = selectedHighlightIds;
        
        const backendIdsToDelete = highlightSuggestions
            .filter(item => idsToDelete.includes(item.id) && item.rowid)
            .map(item => item.rowid as string);
        
        // Make API call if there are items to delete
        if (backendIdsToDelete.length > 0) {
            deleteHighlightSuggestions(
                customer!.name, 
                selectedModel?.value!,
                backendIdsToDelete
            ).then(response => {
                if (response.status === 'error') {
                    console.error("Error deleting highlights:", response.message);
                    // Show error message
                    handleValidationError(response.message);
                }
            }).catch(error => {
                console.error("Error in delete API call:", error);
                handleValidationError("Failed to delete highlights");
            });
        }
        
        // Update the state immediately (optimistic update)
        const remainingItems = highlightSuggestions.filter(item => !idsToDelete.includes(item.id));
        setHighlightSuggestions(remainingItems);
        
        // Remove deleted items from the created and updated lists
        setUpdatedHighlights(prev => prev.filter(item => !idsToDelete.includes(item.id)));
        
        // If everything is deleted, set edited to false
        if (remainingItems.length === 0 || updatedHighlights.length === 0) {
            setHighlightEdited(false);
        }
        
        setSelectedHighlightIds([]);
        setIsDeleting(false);
        setDeleteDialogProps(undefined);
    };

    // Shared delete dialog generator
    const getDeleteDialog = ({
        title,
        itemType,
        onYes,
    }: {
        title: string;
        itemType: string;
        onYes: () => void;
    }) => ({
        ...defaultConfirmationDialogProps,
        title,
        content: (
            <>
                <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                    <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                    <span>This action will remove the selected {itemType} suggestions.</span>
                </div>
                <div style={{ marginTop: 15 }}>
                    Are you sure you want to proceed?
                </div>
            </>
        ),
        onYes,
        yesLabel: 'Yes & Delete',
        closeLabel: 'No',
    });

    // Get delete confirmation dialog for observations
    const getDeleteObservationsDialog = () =>
        getDeleteDialog({
            title: 'Delete Selected Observation Suggestions?',
            itemType: 'observation',
            onYes: deleteSelectedObservations,
        });

    // Get delete confirmation dialog for highlights
    const getDeleteHighlightsDialog = () =>
        getDeleteDialog({
            title: 'Delete Selected Highlight Suggestions?',
            itemType: 'highlight',
            onYes: deleteSelectedHighlights,
        });

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabValue(newValue);
    };

    // Column definitions for observation suggestions grid
    const observationColumns: GridColDef[] = [
        {
            field: 'observation',
            headerName: 'Observation',
            flex: 2,
            editable: false
        },
        {
            field: 'suggestion',
            headerName: 'Suggestion',
            flex: 3,
            editable: true
        }
    ];

    // Column definitions for highlight suggestions grid
    const highlightColumns: GridColDef[] = [
        {
            field: 'highlight',
            headerName: 'Highlight',
            flex: 2,
            editable: false // Make highlight non-editable
        },
        {
            field: 'chat_answer',
            headerName: 'Chat Answer',
            flex: 3,
            editable: false // Make chat_answer non-editable
        },
        {
            field: 'suggestion',
            headerName: 'Suggestion',
            flex: 2,
            editable: true // Only suggestion is editable
        }
    ];

    // Update these handlers to work with the DataGrid's edit model
    const handleObservationEditRowModelChange = (params: any) => {
        const rowId = Object.keys(params.model)[0];
        if (rowId) {
            const field = Object.keys(params.model[rowId])[0];
            const value = params.model[rowId][field].value;
            
            // Find the original item
            const originalItem = observationSuggestions.find(item => item.id === rowId);
            if (originalItem && originalItem[field as keyof ObservationSuggestion] !== value) {
                handleObservationEdit(rowId, field, value);
            }
        }
    };

    const handleHighlightEditRowModelChange = (params: any) => {
        const rowId = Object.keys(params.model)[0];
        if (rowId) {
            const field = Object.keys(params.model[rowId])[0];
            
            // Only allow edits on the suggestion field
            if (field !== 'suggestion') {
                return;
            }
            
            const value = params.model[rowId][field].value;
            
            // Find the original item
            const originalItem = highlightSuggestions.find(item => item.id === rowId);
            
            // Skip the entire update if the value hasn't changed
            if (!originalItem || originalItem[field as keyof HighlightSuggestion] === value) {
                return;
            }
            
            // Only call the edit handler if validation passes
            handleHighlightEdit(rowId, field, value);
        }
    };

    // Update the save functions to send created and updated items
    const saveObservationSuggestions = () => {
        if (observationModel === undefined) return;
        setSaveObservationsLoading(true);
        const requestScope = scopeKeyRef.current;
        
        // Prepare data for the backend - exclude the id field
        const createdItems = createdObservations.map(({ id, ...rest }) => rest);
        const updatedItems = updatedObservations.map(({ id, ...rest }) => rest);
        
        // Combine created and updated items
        const itemsToSave = [...createdItems, ...updatedItems];
        
        // Make API call if there are items to save
        if (itemsToSave.length > 0) {
            upsertObservationSuggestions(
                customer!.name, 
                observationModel,
                createdItems,
                updatedItems
            ).then(response => {
                if (requestScope !== scopeKeyRef.current) return;
                if (response.added.length > 0 || response.updated.length > 0) {
                    // Filter out items that were just added to avoid duplicates
                    const createdIds = createdObservations.map(item => item.id);
                    const filteredExistingItems = observationSuggestions.filter(item => !createdIds.includes(item.id));
                    
                    // Process newly added items (assign them IDs)
                    const newItems = response.added.map(item => ({
                        ...item,
                        id: item.rowid || String(Math.random()) // Create ID from rowid or generate a new one
                    }));
                    
                    // Update existing items with their updated values
                    const updatedItems = [...filteredExistingItems];
                    
                    // Process updated items (update existing items)
                    response.updated.forEach(updatedItem => {
                        // Find the item in our existing list that matches this rowid
                        const index = updatedItems.findIndex(item => item.rowid === updatedItem.rowid);
                        if (index !== -1) {
                            // Update the item in place
                            updatedItems[index] = {
                                ...updatedItem,
                                id: updatedItems[index].id // Preserve the existing ID
                            };
                        }
                    });
                    
                    // Set the state with updated items and new items
                    setObservationSuggestions([...updatedItems, ...newItems]);
                    
                    // Reset tracking state
                    setCreatedObservations([]);
                    setUpdatedObservations([]);
                    setObservationEdited(false);
                }
            }).catch(error => {
                console.error("Error in save API call:", error);
                if (requestScope === scopeKeyRef.current) handleValidationError("Failed to save observations");
            }).finally(() => {
                setSaveObservationsLoading(false);
            });
        } else {
            // Nothing to save
            setSaveObservationsLoading(false);
        }
    };

    // Update the fetchHighlightSuggestions function to also fetch available highlights
    const getHighlightSuggestions = async () => {
        if (!store || !customer || !selectedModel) return;
        
        setHighlightLoading(true);
        try {
            const response = await fetchHighlightSuggestions(
                customer.name,
                selectedModel.value
            );
            
            if (response.data) {
                // Convert HighlightSuggestionModel to HighlightSuggestion by adding id
                const highlightsWithIds: HighlightSuggestion[] = response.data.map(item => ({
                    highlight: item.highlight,
                    suggestion: item.suggestion,
                    chat_answer: item.chat_answer,
                    rowid: item.rowid,
                    id: item.rowid || String(Math.random())
                }));
                setHighlightSuggestions(highlightsWithIds);
            }
        } catch (error) {
            console.error('Error fetching highlight suggestions:', error);
            handleValidationError('Failed to fetch highlight suggestions. Please try again.');
        } finally {
            setHighlightLoading(false);
        }
    };

    // Update the save functions to send created and updated items
    const saveHighlightSuggestions = async () => {
        if (!store || !customer || !selectedModel) return;
        
        setSaveHighlightsLoading(true);
        try {
            // Only send the updated items
            const updatedItems = updatedHighlights.map(item => ({
                highlight: item.highlight,
                suggestion: item.suggestion,
                chat_answer: item.chat_answer,
                rowid: item.rowid
            }));

            const response = await updateHighlightSuggestions(
                customer.name,
                selectedModel.value,
                updatedItems
            );

            if (response.updated.length > 0) {
                // Refresh the suggestions after saving
                await getHighlightSuggestions();
                setHighlightEdited(false);
                setUpdatedHighlights([]);
            }
        } catch (error) {
            console.error('Error saving highlight suggestions:', error);
        } finally {
            setSaveHighlightsLoading(false);
        }
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#F1F2F4', padding: '10px', flex: 1}}>
            <Stack paddingBottom={1} direction="row" spacing={2}>
                <Stack flex={1} justifyContent="center" spacing={1}>
                {customer && (
                    <Alert severity="info" variant="outlined">
                        Managing customer suggestions for: {customer.name}
                    </Alert>
                )}
                {validationError && (
                    <Alert severity="error" variant="outlined">
                        {validationError}
                    </Alert>
                )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography style={{fontWeight: 500}}>Model:</Typography>
                    <SIAutocomplete
                        items={models.map(tc => ({ value: tc.rowid, label: tc.value }))}
                        initial_selection={selectedModel ? { value: selectedModel.rowid, label: selectedModel.value } : undefined} 
                        onSelect={(item?: SelectionItem) => setSelectedModel(item?.value)}
                        style={{ width: '400px' }}
                        loadProgress={() => true}
                        placeholder='Select Model'
                    />
                    {allModelsEnabled && (
                        <FormControlLabel
                            control={<Checkbox checked={allModels} onChange={(e) => setAllModels(e.target.checked)} color="primary" />}
                            label="All models (Obs/Sol only)"
                        />
                    )}
                </Stack>
            </Stack>
            
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Obs/Sol Suggestions" />
                    <Tab label="Highlight Suggestions" />
                </Tabs>

                
                <TabPanel value={tabValue} index={0}>
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '10px 0' }}>
                        {allModels && (
                            <Alert severity="info" variant="outlined" style={{ marginBottom: '10px' }}>
                                These suggestions apply to every model and replace the model-specific suggestions for the same observation.
                            </Alert>
                        )}
                        <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <TextField 
                                label="New Obs/Sol" 
                                variant="outlined" 
                                size="small" 
                                value={newObservation}
                                onChange={(e) => setNewObservation(e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <TextField 
                                label="New Suggestion" 
                                variant="outlined" 
                                size="small" 
                                value={newObservationSuggestion}
                                onChange={(e) => setNewObservationSuggestion(e.target.value)}
                                style={{ flex: 2 }}
                            />
                            <IconButton 
                                color="primary" 
                                onClick={addObservationSuggestion}
                                disabled={observationModel === undefined || !newObservation || !newObservationSuggestion}
                            >
                                <AddIcon />
                            </IconButton>
                            <IconButton 
                                color="secondary" 
                                onClick={() => setDeleteDialogProps(getDeleteObservationsDialog())}
                                disabled={selectedObservationIds.length === 0 || isDeleting}
                            >
                                <DeleteIcon />
                            </IconButton>
                            <Button
                                variant="contained"
                                color="default"
                                startIcon={<SaveIcon />}
                                onClick={saveObservationSuggestions}
                                disabled={observationModel === undefined || saveObservationsLoading || !observationEdited}
                                style={{ minWidth: '100px' }}
                            >
                                {saveObservationsLoading ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                        
                        <div style={{ flex: 1 }}>
                            <DataGrid
                                rows={observationSuggestions}
                                columns={observationColumns}
                                pageSize={5}
                                checkboxSelection
                                disableSelectionOnClick
                                onSelectionModelChange={(model: any) => setSelectedObservationIds(model.selectionModel || model)}
                                selectionModel={selectedObservationIds}
                                onEditRowModelChange={handleObservationEditRowModelChange}
                                loading={loading || isDeleting || saveObservationsLoading}
                            />
                        </div>
                    </div>
                </TabPanel>
                
                <TabPanel value={tabValue} index={1}>
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '10px 0' }}>
                        <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
                            <IconButton 
                                color="secondary" 
                                onClick={() => setDeleteDialogProps(getDeleteHighlightsDialog())}
                                disabled={selectedHighlightIds.length === 0 || isDeleting}
                            >
                                <DeleteIcon />
                            </IconButton>
                            <Button
                                variant="contained"
                                color="default"
                                startIcon={<SaveIcon />}
                                onClick={saveHighlightSuggestions}
                                disabled={saveHighlightsLoading || !highlightEdited}
                                style={{ minWidth: '100px' }}
                            >
                                {saveHighlightsLoading ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                        
                        <div style={{ flex: 1 }}>
                            <DataGrid
                                rows={highlightSuggestions}
                                columns={highlightColumns}
                                pageSize={5}
                                checkboxSelection
                                disableSelectionOnClick
                                onSelectionModelChange={(model: any) => setSelectedHighlightIds(model.selectionModel || model)}
                                selectionModel={selectedHighlightIds}
                                onEditRowModelChange={handleHighlightEditRowModelChange}
                                loading={highlightLoading || isDeleting || saveHighlightsLoading}
                            />
                        </div>
                    </div>
                </TabPanel>
            </div>
            
            {deleteDialogProps && (
                <StepConfirmation
                    confirmationDialogs={[deleteDialogProps]}
                    finish={() => {}}
                    cancel={() => setDeleteDialogProps(undefined)}
                    isValid={true}
                />
            )}
        </div>
    );
});